import axiosInstance from '../../api/axios.js';

/**
 * ID convention for control ↔ risk ↔ process mappings:
 * - **Write (PATCH/POST/query params):** always use record UUIDs (`id` / `*_uuid`).
 * - **Read (GET payloads / UI labels):** use custom display ids (`risk_id`, `process_id`, etc.).
 */

export type ControlRiskMappingWire = {
  /** Risk record UUID (never RSK_xxx). */
  risk_id: string;
  /** Process record UUIDs (never PRC_xxx). */
  process_ids: string[];
};

export async function patchControlRiskMappings(
  controlId: string,
  riskMappings: ControlRiskMappingWire[],
  auditYearId?: string
): Promise<Record<string, unknown>> {
  const userHash = localStorage.getItem('userHash') || '';
  const { data } = await axiosInstance.patch<Record<string, unknown>>(
    `/api/sox-controls/${encodeURIComponent(controlId)}/risk-mappings`,
    {
      risk_mappings: riskMappings,
      ...(auditYearId ? { audit_year_id: auditYearId } : {}),
    },
    { headers: { Authorization: userHash } }
  );
  return data;
}

export async function patchControlUpdateRiskMappings(
  controlId: string,
  payload: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const userHash = localStorage.getItem('userHash') || '';
  const { data } = await axiosInstance.patch<Record<string, unknown>>(
    `/api/sox-controls/${encodeURIComponent(controlId)}/update-control-risk-mappings`,
    payload,
    { headers: { Authorization: userHash } }
  );
  return data;
}

export async function fetchControlRiskFilterOptionsPage({
  page,
  pageSize = 20,
  search = '',
  auditYearId,
}: {
  page: number;
  pageSize?: number;
  search?: string;
  auditYearId?: string | null;
}) {
  const requestedPageSize = Math.max(1, Number(pageSize) || 20);
  const requestedPage = Math.max(1, Number(page) || 1);

  const response = await axiosInstance.get('/api/risks/with-process-mappings', {
    params: {
      page: requestedPage,
      page_size: requestedPageSize,
      ...(search?.trim() ? { search: search.trim() } : {}),
      ...(auditYearId ? { audit_year_id: auditYearId } : {}),
    },
  });

  const body = response.data ?? {};
  const root = body?.data && typeof body.data === 'object' ? body.data : body;
  const items = Array.isArray(root?.items)
    ? root.items
    : Array.isArray(root?.risks)
      ? root.risks
      : Array.isArray(body?.items)
        ? body.items
        : [];

  const mapped = items
    .map((risk: Record<string, unknown>) => {
      /** Picker value = risk record UUID (write). Label uses custom risk_id (read). */
      const value = String(risk?.id ?? '').trim();
      if (!value || !looksLikeUuid(value)) return null;
      const riskId = String(risk?.risk_id ?? '').trim();
      const clientRiskId = String(risk?.client_risk_id ?? '').trim();
      const labelLead = clientRiskId || riskId || value;
      const description = String(risk?.risk_description ?? '').trim();
      return {
        value,
        label: description ? `${labelLead} - ${description}` : labelLead,
        riskCode: clientRiskId || riskId || undefined,
      };
    })
    .filter(Boolean);

  const pag = root?.pagination ?? body?.pagination ?? {};
  const resolvedPage = Number.isFinite(Number(pag.page)) ? Number(pag.page) : requestedPage;
  const totalItems = Number.isFinite(Number(pag.total_items)) ? Number(pag.total_items) : null;
  let totalPages = Number.isFinite(Number(pag.total_pages)) ? Math.max(1, Number(pag.total_pages)) : null;
  if (totalPages == null && totalItems != null && requestedPageSize > 0) {
    totalPages = Math.max(1, Math.ceil(totalItems / requestedPageSize));
  }
  if (totalPages == null) totalPages = 1;

  const isLastPage =
    mapped.length < requestedPageSize || (totalPages > 0 && resolvedPage >= totalPages);

  return {
    items: mapped,
    page: resolvedPage,
    pageSize: requestedPageSize,
    totalPages,
    isLastPage,
  };
}

export async function fetchControlProcessFilterOptionsPage({
  page,
  pageSize = 20,
  search = '',
  riskId,
}: {
  page: number;
  pageSize?: number;
  search?: string;
  riskId: string;
}) {
  const requestedPageSize = Math.max(1, Number(pageSize) || 20);
  const requestedPage = Math.max(1, Number(page) || 1);
  const trimmedRiskId = String(riskId ?? '').trim();
  if (!looksLikeUuid(trimmedRiskId)) {
    return {
      items: [],
      page: requestedPage,
      pageSize: requestedPageSize,
      totalPages: 1,
      isLastPage: true,
    };
  }

  const response = await axiosInstance.get('/api/processes/with-risk-mapping', {
    params: {
      risk_id: trimmedRiskId,
      page: requestedPage,
      page_size: requestedPageSize,
      ...(search?.trim() ? { search: search.trim() } : {}),
      ...(auditYearId ? { audit_year_id: auditYearId } : {}),
    },
  });

  const body = response.data ?? {};
  const root = body?.data && typeof body.data === 'object' ? body.data : body;

  const list = Array.isArray(root?.processes)
    ? root.processes
    : Array.isArray(root?.items)
      ? root.items
      : Array.isArray(body?.items)
        ? body.items
        : [];

  const mapped = list
    .map((process: Record<string, unknown>) => {
      /** Picker value = process UUID (write). Label uses custom process_id (read). */
      const processUuid = extractProcessUuidFromRow(process);
      if (!processUuid) return null;
      const routeProcessId = String(process?.process_id ?? '').trim();
      const rowId = String(process?.id ?? '').trim();
      const cycleId = String(process?.cycle_id ?? '').trim();
      const processName = String(process?.name ?? process?.process_name ?? routeProcessId).trim();
      const cycleName = String(process?.sox_cycle_name ?? process?.cycle_name ?? '').trim();
      const label = cycleName
        ? `${cycleId} - ${routeProcessId} - ${processName} (${cycleName})`
        : `${cycleId} - ${routeProcessId} - ${processName}`;
      return {
        value: processUuid,
        label,
        processUuid,
        routeProcessId: routeProcessId || undefined,
        rowId: rowId || undefined,
      };
    })
    .filter(Boolean);

  const pag = root?.pagination ?? body?.pagination ?? {};
  const resolvedPage = Number.isFinite(Number(pag.page)) ? Number(pag.page) : requestedPage;
  const totalItems = Number.isFinite(Number(pag.total_items)) ? Number(pag.total_items) : null;
  let totalPages = Number.isFinite(Number(pag.total_pages)) ? Math.max(1, Number(pag.total_pages)) : null;
  if (totalPages == null && totalItems != null && requestedPageSize > 0) {
    totalPages = Math.max(1, Math.ceil(totalItems / requestedPageSize));
  }
  if (totalPages == null) totalPages = 1;

  const isLastPage =
    mapped.length < requestedPageSize || (totalPages > 0 && resolvedPage >= totalPages);

  return {
    items: mapped,
    page: resolvedPage,
    pageSize: requestedPageSize,
    totalPages,
    isLastPage,
  };
}

export type ControlRiskAssociation = {
  riskId: string;
  mitigatingProcessIds: string[];
};

function isControlRiskAssociation(row: ControlRiskAssociation | null): row is ControlRiskAssociation {
  return row != null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function looksLikeUuid(value: string): boolean {
  return UUID_RE.test(value);
}

/** Risk record UUID from a GET `risk_mappings` row (for PATCH / query params). */
export function extractRiskMappingRiskUuid(row: Record<string, unknown>): string {
  const rowId = String(row?.id ?? '').trim();
  if (looksLikeUuid(rowId)) return rowId;

  const riskUuid = String(row?.risk_uuid ?? row?.riskUuid ?? '').trim();
  const riskIdField = String(row?.risk_id ?? row?.riskId ?? '').trim();

  if (riskUuid && looksLikeUuid(riskUuid)) return riskUuid;
  if (riskIdField && looksLikeUuid(riskIdField)) return riskIdField;
  return '';
}

/** Custom display risk id from GET (e.g. RSK_000408) — never used in PATCH bodies. */
export function extractRiskMappingDisplayRiskId(row: Record<string, unknown>): string {
  const clientRiskId = String(row?.client_risk_id ?? row?.clientRiskId ?? '').trim();
  if (clientRiskId) return clientRiskId;
  const riskCode = String(row?.risk_id ?? row?.riskId ?? '').trim();
  if (riskCode && !looksLikeUuid(riskCode)) return riskCode;
  return '';
}

/**
 * Form association key for a risk row: UUID when GET provides it, otherwise display id
 * (resolved to UUID on save via `resolveWireRiskId`).
 */
export function extractRiskMappingRiskId(row: Record<string, unknown>): string {
  const riskUuid = extractRiskMappingRiskUuid(row);
  if (riskUuid) return riskUuid;
  const displayId = extractRiskMappingDisplayRiskId(row);
  if (displayId) return displayId;
  return String(row?.id ?? '').trim();
}

/** Custom display process id from GET (e.g. PRC_000182). */
export function extractProcessDisplayId(process: Record<string, unknown>): string {
  return String(process?.process_id ?? process?.processId ?? '').trim();
}

function extractMitigatingProcessIds(row: Record<string, unknown>): string[] {
  const processes = Array.isArray(row?.processes) ? row.processes : [];
  if (processes.length > 0) {
    const wireIds = processes
      .map((process: Record<string, unknown>) => extractProcessUuidFromRow(process))
      .filter((id) => looksLikeUuid(id));
    if (wireIds.length > 0) return wireIds;
  }

  /** Legacy GET rows may only expose custom ids in `process_ids`; resolved on PATCH. */
  return (Array.isArray(row?.process_ids) ? row.process_ids : [])
    .map((processId: unknown) => String(processId ?? '').trim())
    .filter(Boolean);
}

/** Canonical process UUID from a `risk_mappings.processes[]` row. */
export function extractProcessUuidFromRow(process: Record<string, unknown>): string {
  const processUuid = String(process?.process_uuid ?? process?.processUuid ?? '').trim();
  if (looksLikeUuid(processUuid)) return processUuid;
  const idField = String(process?.id ?? '').trim();
  if (looksLikeUuid(idField)) return idField;
  return '';
}

function processRowMatchesCandidate(
  process: Record<string, unknown>,
  candidate: string
): boolean {
  const keys = [
    process?.process_uuid,
    process?.processUuid,
    process?.process_id,
    process?.id,
  ]
    .map((key) => String(key ?? '').trim())
    .filter(Boolean);
  return keys.includes(candidate);
}

function formatProcessMappingLabel(process: Record<string, unknown>): string {
  const cycleId = String(process?.cycle_id ?? '').trim();
  const routeProcessId = String(process?.process_id ?? '').trim();
  const processName = String(process?.process_name ?? process?.name ?? routeProcessId).trim();
  const cycleName = String(process?.cycle_name ?? '').trim();
  return cycleName
    ? `${cycleId} - ${routeProcessId} - ${processName} (${cycleName})`
    : `${cycleId} - ${routeProcessId} - ${processName}`;
}

function registerProcessLabelKeys(
  labels: Record<string, string>,
  process: Record<string, unknown>,
  label: string
): void {
  const displayId = extractProcessDisplayId(process);
  const wireId = extractProcessUuidFromRow(process);
  const keys = [displayId, wireId, process?.id]
    .map((key) => String(key ?? '').trim())
    .filter(Boolean);
  for (const key of keys) {
    if (!labels[key]) labels[key] = label;
  }
}

export function readControlRiskMappings(
  payload: Record<string, unknown> | null | undefined
): Array<Record<string, unknown>> {
  if (!payload || typeof payload !== 'object') return [];
  if (Array.isArray(payload.risk_mappings)) return payload.risk_mappings;

  const nestedControl = payload.control;
  if (nestedControl && typeof nestedControl === 'object') {
    const fromControl = readControlRiskMappings(nestedControl as Record<string, unknown>);
    if (fromControl.length > 0) return fromControl;
  }

  const nestedData = payload.data;
  if (nestedData && typeof nestedData === 'object') {
    return readControlRiskMappings(nestedData as Record<string, unknown>);
  }

  return [];
}

export function extractControlRiskAssociations(
  control: Record<string, unknown> | null | undefined
): ControlRiskAssociation[] {
  const mappings = readControlRiskMappings(control);
  return mappings
    .map((row: Record<string, unknown>) => {
      const riskId = extractRiskMappingRiskId(row);
      if (!riskId) return null;
      return { riskId, mitigatingProcessIds: extractMitigatingProcessIds(row) };
    })
    .filter(isControlRiskAssociation);
}

/** Labels for risks linked on a control, derived from server `risk_mappings`. */
export function buildRiskLabelsFromMappings(
  mappings: Array<Record<string, unknown>>
): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const mapping of mappings) {
    const displayId = extractRiskMappingDisplayRiskId(mapping);
    const riskUuid = extractRiskMappingRiskUuid(mapping);
    const description = String(mapping?.risk_description ?? '').trim();
    const labelLead = displayId || riskUuid || String(mapping?.id ?? '').trim();
    const label = description ? `${labelLead} - ${description}` : labelLead;

    const keys = [
      riskUuid,
      displayId,
      extractRiskMappingRiskId(mapping),
      String(mapping?.id ?? '').trim(),
    ].filter(Boolean);
    for (const key of keys) {
      if (!labels[key]) labels[key] = label;
    }
  }
  return labels;
}

/** Labels for processes on a single `risk_mappings` row. */
export function buildProcessLabelsFromMappingRow(
  mapping: Record<string, unknown>
): Record<string, string> {
  const labels: Record<string, string> = {};
  const processes = Array.isArray(mapping?.processes) ? mapping.processes : [];
  for (const process of processes) {
    registerProcessLabelKeys(labels, process as Record<string, unknown>, formatProcessMappingLabel(process as Record<string, unknown>));
  }
  return labels;
}

export function findRiskMappingRowForFormRiskId(
  mappings: Array<Record<string, unknown>>,
  riskId: string
): Record<string, unknown> | undefined {
  const trimmedRiskId = String(riskId ?? '').trim();
  if (!trimmedRiskId) return undefined;
  return mappings.find((row) => {
    const rowKeys = [
      extractRiskMappingRiskId(row),
      String(row?.risk_id ?? '').trim(),
      String(row?.id ?? '').trim(),
    ].filter(Boolean);
    return rowKeys.includes(trimmedRiskId);
  });
}

/** Picker options for processes on a `risk_mappings` row (includes previously mapped processes). */
export function buildProcessOptionsFromMappingRow(
  mapping: Record<string, unknown>
): Array<{
  value: string;
  label: string;
  processUuid?: string;
  routeProcessId?: string;
  rowId?: string;
}> {
  const processes = Array.isArray(mapping?.processes) ? mapping.processes : [];
  const options: Array<{
    value: string;
    label: string;
    processUuid?: string;
    routeProcessId?: string;
    rowId?: string;
  }> = [];
  const seen = new Set<string>();

  for (const process of processes) {
    const processUuid = extractProcessUuidFromRow(process as Record<string, unknown>);
    const routeProcessId = extractProcessDisplayId(process as Record<string, unknown>);
    const rowId = String(process?.id ?? '').trim();
    /** Picker/form wire value is always UUID when available. */
    const value = processUuid || routeProcessId || rowId;
    if (!value || seen.has(value)) continue;
    seen.add(value);
    options.push({
      value: processUuid || value,
      label: formatProcessMappingLabel(process as Record<string, unknown>),
      processUuid: processUuid || undefined,
      routeProcessId: routeProcessId || undefined,
      rowId: rowId || undefined,
    });
  }

  return options;
}

/** Labels for mapped processes on a control, derived from server `risk_mappings`. */
export function buildProcessLabelsFromMappings(
  mappings: Array<Record<string, unknown>>
): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const mapping of mappings) {
    Object.assign(labels, buildProcessLabelsFromMappingRow(mapping));
  }
  return labels;
}

export type ControlProcessWireOption = {
  value: string;
  label?: string;
  processUuid?: string;
  routeProcessId?: string;
  rowId?: string;
};

export type ControlRiskMappingsWireContext = {
  riskOptions?: Array<{ value: string; label?: string; riskCode?: string }>;
  riskMappings?: Array<Record<string, unknown>>;
  processOptions?: ControlProcessWireOption[];
};

function riskLabelMatchesId(label: string, candidateId: string): boolean {
  const trimmed = candidateId.trim();
  if (!trimmed) return false;
  return label === trimmed || label.startsWith(`${trimmed} -`);
}

function riskMappingRowMatchesFormRiskId(
  mapping: Record<string, unknown>,
  trimmedRiskId: string
): boolean {
  const rowKeys = [
    extractRiskMappingRiskUuid(mapping),
    extractRiskMappingRiskId(mapping),
    String(mapping?.risk_id ?? '').trim(),
    String(mapping?.client_risk_id ?? '').trim(),
    String(mapping?.id ?? '').trim(),
  ].filter(Boolean);
  return rowKeys.includes(trimmedRiskId);
}

/** Resolve a display/client risk code to the risk record UUID via picker options. */
export function resolveRiskUuidFromRiskOptions(
  candidate: string,
  riskOptions: Array<{ value: string; label?: string; riskCode?: string }> = []
): string {
  const trimmed = String(candidate ?? '').trim();
  if (!trimmed) return '';
  if (looksLikeUuid(trimmed)) return trimmed;

  for (const option of riskOptions) {
    const value = String(option.value ?? '').trim();
    if (value === trimmed && looksLikeUuid(value)) return value;
  }

  for (const option of riskOptions) {
    const value = String(option.value ?? '').trim();
    if (!looksLikeUuid(value)) continue;
    const riskCode = String(option.riskCode ?? '').trim();
    if (riskCode && riskCode === trimmed) return value;
    if (riskLabelMatchesId(String(option.label ?? ''), trimmed)) return value;
  }

  return '';
}

function resolveProcessUuidFromOptions(
  candidate: string,
  processOptions: ControlProcessWireOption[] = []
): string {
  const trimmed = String(candidate ?? '').trim();
  if (!trimmed) return '';
  if (looksLikeUuid(trimmed)) return trimmed;

  for (const option of processOptions) {
    const value = String(option.value ?? '').trim();
    if (value === trimmed && looksLikeUuid(value)) return value;
  }

  for (const option of processOptions) {
    const processUuid = String(option.processUuid ?? '').trim();
    const routeProcessId = String(option.routeProcessId ?? '').trim();
    const rowId = String(option.rowId ?? '').trim();
    const value = String(option.value ?? '').trim();

    if (routeProcessId === trimmed && looksLikeUuid(processUuid)) return processUuid;
    if (routeProcessId === trimmed && looksLikeUuid(value)) return value;
    if (rowId === trimmed && looksLikeUuid(processUuid)) return processUuid;
    if (rowId === trimmed && looksLikeUuid(value)) return value;
  }

  return '';
}

function resolveProcessUuidFromMappings(
  candidate: string,
  riskMappings: Array<Record<string, unknown>>,
  formRiskId?: string
): string {
  const trimmed = String(candidate ?? '').trim();
  if (!trimmed) return '';
  if (looksLikeUuid(trimmed)) return trimmed;

  const rows = formRiskId
    ? [findRiskMappingRowForFormRiskId(riskMappings, formRiskId)].filter(
        (row): row is Record<string, unknown> => row != null
      )
    : riskMappings;

  for (const mapping of rows) {
    const processes = Array.isArray(mapping?.processes) ? mapping.processes : [];
    for (const process of processes) {
      if (!processRowMatchesCandidate(process as Record<string, unknown>, trimmed)) continue;
      const uuid = extractProcessUuidFromRow(process as Record<string, unknown>);
      if (looksLikeUuid(uuid)) return uuid;
    }
  }

  return '';
}

/** Resolve a form/picker process id to the process UUID required by PATCH. */
export function resolveWireProcessId(
  processId: string,
  context: ControlRiskMappingsWireContext & { formRiskId?: string } = {}
): string {
  const trimmed = String(processId ?? '').trim();
  if (!trimmed) return '';
  if (looksLikeUuid(trimmed)) return trimmed;

  const { riskMappings = [], processOptions = [], formRiskId } = context;

  const fromMappings = resolveProcessUuidFromMappings(trimmed, riskMappings, formRiskId);
  if (looksLikeUuid(fromMappings)) return fromMappings;

  const fromAllMappings = resolveProcessUuidFromMappings(trimmed, riskMappings);
  if (looksLikeUuid(fromAllMappings)) return fromAllMappings;

  const fromOptions = resolveProcessUuidFromOptions(trimmed, processOptions);
  if (looksLikeUuid(fromOptions)) return fromOptions;

  return '';
}

/** Resolve a form/picker id to the risk record UUID required by PATCH and process APIs. */
export function resolveWireRiskId(
  riskId: string,
  context: ControlRiskMappingsWireContext = {}
): string {
  const trimmed = String(riskId ?? '').trim();
  if (!trimmed) return '';
  if (looksLikeUuid(trimmed)) return trimmed;

  const { riskOptions = [], riskMappings = [] } = context;

  for (const mapping of riskMappings) {
    if (!riskMappingRowMatchesFormRiskId(mapping, trimmed)) continue;
    const uuid = extractRiskMappingRiskUuid(mapping);
    if (looksLikeUuid(uuid)) return uuid;
    const riskCode = String(mapping?.risk_id ?? mapping?.client_risk_id ?? '').trim();
    const fromOptions = resolveRiskUuidFromRiskOptions(riskCode || trimmed, riskOptions);
    if (looksLikeUuid(fromOptions)) return fromOptions;
  }

  const fromOptions = resolveRiskUuidFromRiskOptions(trimmed, riskOptions);
  if (looksLikeUuid(fromOptions)) return fromOptions;

  return '';
}

export function toControlRiskMappingsWire(
  associations: Array<{ riskId: string; mitigatingProcessIds: string[] }>,
  context: ControlRiskMappingsWireContext = {}
): ControlRiskMappingWire[] {
  return associations
    .map((row) => {
      const risk_id = resolveWireRiskId(row.riskId, context);
      if (!risk_id || !looksLikeUuid(risk_id)) return null;

      const process_ids = (row.mitigatingProcessIds ?? [])
        .map((processId) =>
          resolveWireProcessId(processId, { ...context, formRiskId: row.riskId })
        )
        .filter((processId) => looksLikeUuid(processId));

      if (process_ids.length === 0 && (row.mitigatingProcessIds ?? []).length > 0) {
        return null;
      }

      return { risk_id, process_ids };
    })
    .filter((row): row is ControlRiskMappingWire => row != null);
}
